import { Loader2 } from "lucide-react";
import { useBackupHistory } from "../../lib/adminQueues";
import { fmtSize, nightOf, lastNights } from "../../lib/backups";
import { StatCard, EmptyState } from "./_ui";
import { BackupNightStrip } from "./BackupNightStrip";
import { BackupTable } from "./BackupTable";

const NIGHTS = 30;
const TABLE_ROWS = 30;

const hoursAgo = (iso) => Math.round((Date.now() - new Date(iso).getTime()) / 3600000);

/** Backups tab: 30-night gap strip, headline numbers, and the last 30 runs. */
export const BackupsTab = () => {
  const { rows, ttlDays, loading, error, refresh } = useBackupHistory({ days: NIGHTS, limit: 200 });

  const ready = rows.filter((r) => r.status === "ready");
  const lastReady = ready[0];
  const nights = lastNights(NIGHTS);
  const covered = new Set(ready.map((r) => nightOf(r.created_at)));
  const gaps = nights.slice(0, -1).filter((d) => !covered.has(d)).length;
  const failedNights = new Set(rows.filter((r) => r.status === "failed").map((r) => nightOf(r.created_at))).size;
  const downloads = rows.reduce((n, r) => n + (r.downloads || 0), 0);
  const stored = ready.reduce((n, r) => n + (r.size_bytes || 0), 0);

  return (
    <div data-testid="tab-backups" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">Off-platform copies</p>
          <h2 className="mt-2 font-serif text-3xl leading-tight text-ink">Every night at 1:00 AM Pacific.</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-olive">
            Each run zips the whole database to object storage and emails a private link that expires after {ttlDays} days. Grey tiles are nights with no copy; that is the gap to chase.
          </p>
        </div>
        <button type="button" onClick={refresh} data-testid="backups-refresh" className="text-xs font-semibold uppercase tracking-[0.18em] text-olive hover:text-ink">Refresh</button>
      </div>

      {error && <p className="rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet">{error}</p>}

      {loading ? (
        <div className="flex items-center gap-2 text-olive"><Loader2 className="h-5 w-5 animate-spin" /> Loading backup history…</div>
      ) : rows.length === 0 ? (
        <EmptyState title="No copies yet." description="The first nightly run lands at 1:00 AM Pacific, or press Send nightly copy now to make one immediately." />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard label="Last good copy" value={lastReady ? `${hoursAgo(lastReady.created_at)}h ago` : "none"} accent={lastReady && hoursAgo(lastReady.created_at) <= 36 ? "grass" : "tangerine"} helper={lastReady ? `${lastReady.collections} collections · ${fmtSize(lastReady.size_bytes)}` : "No ready copy in the window."} testid="backup-stat-last" />
            <StatCard label={`Gaps in ${NIGHTS} nights`} value={gaps} accent={gaps ? "tangerine" : "grass"} helper={`Nights with no ready copy, excluding tonight${failedNights ? ` (${failedNights} of them failed runs)` : ""}.`} testid="backup-stat-gaps" />
            <StatCard label="Downloads" value={downloads} helper="Times an emailed link was opened." testid="backup-stat-downloads" />
            <StatCard label="Stored" value={fmtSize(stored)} helper={`${ready.length} zip${ready.length === 1 ? "" : "s"} kept in object storage.`} testid="backup-stat-stored" />
          </div>

          <BackupNightStrip rows={rows} nights={NIGHTS} />

          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">Last {Math.min(TABLE_ROWS, rows.length)} runs</p>
            <BackupTable rows={rows.slice(0, TABLE_ROWS)} />
          </div>
        </>
      )}
    </div>
  );
};
